<template>
  <v-container v-if="currentApp">
    <v-toolbar 
      color="transparent" 
      extension-height="100"
      flat
      extended 
      class="mb-5" 
    >  
      <v-row align="center" key="truc">
        <v-col cols="grow">
          <v-toolbar-title class="text-xl-h4 text-lg-h5 font-weight-bold" :key="'title_' + currentApp.id">
            {{ currentApp.name }}
          </v-toolbar-title>
        </v-col>
        <v-col cols="4">
          <v-text-field 
            v-if="isEditing"
            :label="$t('Page.EditForm.title')"
            prepend-inner-icon="layers" 
            v-model="currentPage.name" 
            hide-details
            outlined
            dense
          >
            <template v-slot:prepend>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn small icon color="primary" v-bind="attrs" v-on="on" @click="showLayoutGrid = true">
                    <v-icon>auto_awesome_mosaic</v-icon>
                  </v-btn>
                </template>
                {{ $t('Page.Grid.editor') }}
              </v-tooltip>
              
            </template>
            <template v-slot:append-outer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn small icon color="error" v-bind="attrs" v-on="on" @click="isDeleting = true">
                    <v-icon>delete</v-icon>
                  </v-btn>
                </template>
                {{ $t('Global.Action.delete') }}
              </v-tooltip>
            </template>
          </v-text-field>

          <v-select 
            v-else
            v-model="selectedPageId" 
            :items="currentAppPages" 
            item-text="name" 
            item-value="id"
            prepend-inner-icon="layers" 
            hide-details 
            outlined
            dense
          >
            <template v-slot:prepend>
              <v-btn small icon color="secondary" @click="prevPage" :disabled="currentPageIndex === 0">
                <v-icon>navigate_before</v-icon>
              </v-btn>
            </template>
            <template v-slot:append-item>
              <v-list-item>
                <v-btn block color="primary" small @click="isCreating = true">
                  <v-icon left>add</v-icon>
                  Nouvelle page
                </v-btn>
              </v-list-item>
            </template>
            <template v-slot:append-outer>
              <v-btn small icon color="secondary" @click="nextPage" :disabled="currentPageIndex === currentAppPages.length - 1">
                <v-icon>navigate_next</v-icon>
              </v-btn>
            </template>
          </v-select>
          
        </v-col>
        <v-col cols="grow">
          
          <PageActions 
            v-if="!!currentPage && canEdit"
            :isEditing="isEditing" 
            :isManaged="currentPage.isManaged" 
            :isPublished="currentPage.isPublished" 
            @edit="isEditing = true" 
            @publish="switchIsPublished" 
            @unpublish="switchIsPublished" 
            @cancel="cancelEdit" 
            @save="updatePage"
          />
        </v-col>
      </v-row>
      
      <template v-slot:extension>
        <v-row align="center">
          <template v-if="isEditing">
            <v-spacer></v-spacer>
            <v-col cols="3" v-if="currentPage.isManaged">
              <GroupSelector :groups.sync="currentPage.groups" outline dense/>
            </v-col>
          </template>
          <template v-else>
            <v-col cols="auto" v-if="currentApp.filters.length">
              <v-btn 
                :input-value="showFilters" 
                color="primary" 
                icon
                @click="showFilters = !showFilters"
              >
                <v-icon>filter_alt</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="10">
              <QlikSelections :apps="[currentApp]" :key="'selection_' + currentApp.qAppId" showEmpty/>
            </v-col>
            <v-col v-show="showFilters" cols="12" lg="2" v-for="filter in currentApp.filters" :key="'selection_' + currentApp.qAppId + filter">
              <QlikCustomFilter 
                :qDimensionId="filter" 
                :qAppId="currentApp.qAppId" 
                showAll 
                multiple 
                dense 
                outlined
              />
            </v-col>
          </template>
        </v-row>
      </template>
    </v-toolbar>
    
    <v-dialog v-model="isCreating" max-width="700">
      <PageEditForm 
        @submit="createPage"
        :models="currentAppPages"
      />
    </v-dialog>
    

    <template v-if="currentPage">
      <v-dialog
        v-if="isEditing"
        v-model="showLayoutGrid"
        fullscreen
        hide-overlay
        transition="dialog-bottom-transition"
      >
        <v-sheet min-height="100%" color="background">
          <PageLayoutEditor 
            :layout.sync="currentPage.layout" 
            :key="'page_grid_' + currentPage.id"
            @finish="showLayoutGrid = false"
          />
        </v-sheet>
      </v-dialog>
      
      <PageLayoutRender 
        v-if="!showLayoutGrid"
        :layout.sync="currentPage.layout"  
        :enableSwitch="isEditing" 
        :app="currentApp" 
        :key="'page_render_' + currentPage.id"
        :showEmpty="isEditing"
      />
    </template>

    <ConfirmDialog 
      :show="isDeleting" 
      :description="$t('Page.Warning.confirmDeleting')" 
      @cancel="isDeleting = false" 
      @confirm="deletePage"
    />
  </v-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import PageEditForm from '@/components/Page/PageEditForm'
import PageActions from '@/components/Page/PageActions'
import PageLayoutEditor from '@/components/Page/PageLayoutEditor'
import PageLayoutRender from '@/components/Page/PageLayoutRender'
import QlikSelections from '@/components/Qlik/QlikSelections'
import QlikCustomFilter from '@/components/Qlik/QlikCustomFilter'
import GroupSelector from '@/components/Utils/GroupSelector'
import ConfirmDialog from '@/components/Utils/ConfirmDialog'

export default {
  components: { 
    PageLayoutEditor,
    PageLayoutRender, 
    PageEditForm,
    PageActions,
    QlikSelections,
    QlikCustomFilter,
    GroupSelector,
    ConfirmDialog
  },
  data: () => ({
    selectedPageId: null,
    currentPage: null,
    isEditing: false,
    isCreating: false,
    isDeleting: false,
    showLayoutGrid: false,
    showFilters: false
  }),
  computed: {
    ...mapState('qlik', [
      'apps'
    ]),
    ...mapState('page', [
      'pages'
    ]),
    ...mapState('auth', [
      'user'
    ]),
    ...mapGetters('auth', [
      'isAdmin'
    ]),
    currentApp () {
      return this.apps.find(app => app.id == this.$route.params.id)
    },
    currentAppPages () {
      if (!this.currentApp) return []
      const pages = [...this.pages.filter(page => page.qlikApp === '/qlik_apps/' + this.currentApp.id)]
      return pages.sort((a, b) => a.name > b.name ? 1 : -1)
    },
    canEdit () {
      return this.isAdmin || this.currentPage?.owner === '/users/' + this.user.id
    },
    currentPageIndex () {
      return this.currentAppPages.findIndex(page => page.id === this.selectedPageId)
    }
  },
  watch: {
    selectedPageId: function (id) {
      this.isEditing = false
      if (id) {
        const page = this.currentAppPages.find(page => page.id === id)
        this.currentPage = JSON.parse(JSON.stringify(page))
      } else {
        this.currentPage = null
      }
    },
    currentApp: {
      immediate: true,
      handler: function () {
        this.isEditing = false
        this.selectedPageId = this.currentAppPages[0]?.id
        if (!this.selectedPageId) {
          this.isCreating = true
        }
      }
    } 
  },
  methods: {
    async createPage (page) {
      const newPage = await this.$store.dispatch('page/createPage', {
        ...page, 
        qlikApp: {id: this.currentApp.id
      }})
      this.isCreating = false
      this.selectedPageId = newPage.id
      await this.$nextTick()
      this.isEditing = true
    },
    async deletePage () {
      await this.$store.dispatch('page/deletePage', this.currentPage.id)
      this.isEditing = false
      this.isDeleting = false
      this.selectedPageId = this.currentAppPages[0]?.id
    },
    async updatePage () {
      await this.$store.dispatch('page/updatePage', this.currentPage)
      this.isEditing = false
    },
    cancelEdit () {
      this.isEditing = false
      const page = this.currentAppPages.find(page => page.id === this.currentPage.id)
      this.currentPage = JSON.parse(JSON.stringify(page))
    },
    switchIsPublished () {
      this.currentPage.isPublished = !this.currentPage.isPublished
      this.updatePage()
    },
    nextPage () {
      this.selectedPageId = this.currentAppPages[this.currentPageIndex + 1]?.id || this.selectedPageId
    },
    prevPage () {
      this.selectedPageId = this.currentAppPages[this.currentPageIndex - 1]?.id || this.selectedPageId
    }
  }
}
</script>